"use client";

import {
  Children,
  Fragment,
  forwardRef,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "@/lib/cn";
import type { InputSize } from "./input";

/**
 * Custom popover <select>.
 *
 * Drop-in replacement for the native control: it accepts the same
 * `<option>` / `<optgroup>` children, the same controlled `value` +
 * `onChange(e => e.target.value)` contract, and the same `size` /
 * `invalid` / `disabled` chrome props as `Input`/`Button`, so existing
 * call sites need no changes.
 *
 * Why custom instead of native: the browser-native option list ignores
 * the app's dark theme and renders as light OS chrome that reads as
 * foreign inside the operator surfaces. This component renders the list
 * itself — themed, keyboard-navigable, type-ahead searchable — and
 * portals the panel to `document.body` so it is never clipped by a
 * modal's or table's `overflow:hidden`.
 *
 * The closed trigger is pixel-matched to `Input` (height, border, focus
 * ring, padding) so a Select and an Input sitting side by side line up.
 */

// Kept pixel-matched to Input (incl. the readable 13/14px text floor).
const SIZE_CLS: Record<InputSize, string> = {
  sm: "h-7 pl-2 pr-7 text-[13px]",
  md: "h-8 pl-2.5 pr-8 text-[14px]",
};
const CHEVRON_OFFSET: Record<InputSize, string> = { sm: "right-1.5", md: "right-2" };

type Item = { value: string; label: string; disabled?: boolean };
type Group = { label: string | null; items: Item[] };

/** Flatten a node's text content into the searchable / displayed label. */
function nodeText(node: ReactNode): string {
  if (node == null || node === false || node === true) return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(nodeText).join("");
  if (isValidElement(node)) return nodeText((node.props as { children?: ReactNode }).children);
  return "";
}

function optionItem(el: React.ReactElement): Item {
  const op = el.props as { value?: string | number; disabled?: boolean; children?: ReactNode };
  return {
    value: String(op.value ?? nodeText(op.children)),
    label: nodeText(op.children),
    disabled: op.disabled,
  };
}

/** Walk `<option>` / `<optgroup>` children into a grouped item model. */
function parseChildren(children: ReactNode): Group[] {
  const groups: Group[] = [];
  let loose: Item[] | null = null;

  const pushLoose = (item: Item) => {
    if (!loose) {
      loose = [];
      groups.push({ label: null, items: loose });
    }
    loose.push(item);
  };

  const walk = (nodes: ReactNode) => {
    Children.forEach(nodes, (child) => {
      if (!isValidElement(child)) return;
      // Transparently descend into fragments so `<>…</>` around options works.
      if (child.type === Fragment) {
        walk((child.props as { children?: ReactNode }).children);
        return;
      }
      if (child.type === "optgroup") {
        const props = child.props as { label?: string; children?: ReactNode };
        const items: Item[] = [];
        Children.forEach(props.children, (opt) => {
          if (isValidElement(opt) && opt.type === "option") items.push(optionItem(opt));
        });
        groups.push({ label: props.label ?? null, items });
        loose = null;
        return;
      }
      if (child.type === "option") pushLoose(optionItem(child));
    });
  };

  walk(children);
  return groups;
}

export type SelectProps = Omit<
  React.SelectHTMLAttributes<HTMLSelectElement>,
  "size" | "onChange"
> & {
  size?: InputSize;
  invalid?: boolean;
  /**
   * Same shape as the native handler. Only `value` is synthesized on the
   * event, which is all every call site reads.
   */
  onChange?: (e: { target: { value: string }; currentTarget: { value: string } }) => void;
};

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  {
    size = "md",
    invalid,
    className,
    children,
    value,
    defaultValue,
    onChange,
    disabled,
    name,
    id,
    required,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledby,
    "aria-invalid": ariaInvalid,
    ...rest
  },
  ref,
) {
  const groups = useMemo(() => parseChildren(children), [children]);
  const flat = useMemo(() => groups.flatMap((g) => g.items), [groups]);

  const isControlled = value !== undefined;
  const [internal, setInternal] = useState<string>(() =>
    value !== undefined
      ? String(value)
      : defaultValue !== undefined
        ? String(defaultValue)
        : (flat[0]?.value ?? ""),
  );
  const current = isControlled ? String(value) : internal;
  const selected = flat.find((it) => it.value === current);

  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const typeahead = useRef<{ buf: string; t: number }>({ buf: "", t: 0 });

  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  // Satisfy the forwarded HTMLSelectElement ref contract for legacy callers;
  // expose the trigger's focus so `ref.current?.focus()` still works.
  useImperativeHandle(ref, () => triggerRef.current as unknown as HTMLSelectElement, []);

  const reactId = useId();
  const listId = `${id ?? reactId}-listbox`;

  const commit = useCallback(
    (val: string) => {
      if (!isControlled) setInternal(val);
      onChange?.({ target: { value: val }, currentTarget: { value: val } });
    },
    [isControlled, onChange],
  );

  // --- positioning (portal) ---------------------------------------------
  const [pos, setPos] = useState<{ left: number; top: number; width: number; drop: "down" | "up"; maxH: number } | null>(
    null,
  );
  const place = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const below = window.innerHeight - r.bottom;
    const above = r.top;
    const drop = below < 240 && above > below ? "up" : "down";
    const maxH = Math.max(160, Math.min(320, (drop === "down" ? below : above) - 12));
    // The panel follows the trigger's width, but never below a readable floor:
    // a trigger that shrink-wraps a short label (a dash, "All") would otherwise
    // render its options as a column of ellipses. Stay inside the viewport.
    const width = Math.min(Math.max(r.width, 240), window.innerWidth - 16);
    const left = Math.max(8, Math.min(r.left, window.innerWidth - width - 8));
    setPos({ left, top: drop === "down" ? r.bottom + 4 : r.top - 4, width, drop, maxH });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    place();
    const onScroll = () => place();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [open, place]);

  // Open with the current selection (or first enabled) highlighted.
  useEffect(() => {
    if (!open) return;
    const idx = flat.findIndex((it) => it.value === current);
    setActiveIdx(idx >= 0 ? idx : 0);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Outside click + Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  // Keep the active row scrolled into view.
  useEffect(() => {
    if (!open) return;
    panelRef.current?.querySelector<HTMLElement>(`[data-idx="${activeIdx}"]`)?.scrollIntoView({ block: "nearest" });
  }, [activeIdx, open]);

  const moveActive = useCallback(
    (dir: 1 | -1) => {
      if (!flat.length) return;
      setActiveIdx((i) => {
        let n = i;
        for (let step = 0; step < flat.length; step++) {
          n = (n + dir + flat.length) % flat.length;
          if (!flat[n]?.disabled) break;
        }
        return n;
      });
    },
    [flat],
  );

  const onTriggerKey = (e: React.KeyboardEvent) => {
    if (disabled) return;
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        moveActive(1);
        break;
      case "ArrowUp":
        e.preventDefault();
        moveActive(-1);
        break;
      case "Home":
        e.preventDefault();
        setActiveIdx(flat.findIndex((it) => !it.disabled));
        break;
      case "End":
        e.preventDefault();
        setActiveIdx(flat.length - 1);
        break;
      case "Enter":
      case " ": {
        e.preventDefault();
        const it = flat[activeIdx];
        if (it && !it.disabled) {
          commit(it.value);
          setOpen(false);
          triggerRef.current?.focus();
        }
        break;
      }
      case "Escape":
        e.preventDefault();
        setOpen(false);
        triggerRef.current?.focus();
        break;
      case "Tab":
        setOpen(false);
        break;
      default:
        // type-ahead
        if (e.key.length === 1) {
          const now = Date.now();
          const buf = now - typeahead.current.t < 700 ? typeahead.current.buf + e.key : e.key;
          typeahead.current = { buf, t: now };
          const lower = buf.toLowerCase();
          const hit = flat.findIndex((it) => !it.disabled && it.label.toLowerCase().startsWith(lower));
          if (hit >= 0) setActiveIdx(hit);
        }
    }
  };

  return (
    // The caller's `className` drives width/layout (`w-full`, `max-w-*`,
    // `flex-1`, `col-span-*`) on the wrapper; the trigger keeps the fixed
    // Input-matched chrome so a Select and an Input line up everywhere.
    <div className={cn("relative inline-block", disabled && "opacity-60", className)}>
      {name && <input type="hidden" name={name} value={current} />}
      <button
        ref={triggerRef}
        type="button"
        id={id}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledby}
        aria-invalid={invalid || ariaInvalid || undefined}
        aria-required={required || undefined}
        disabled={disabled}
        onClick={() => !disabled && setOpen((v) => !v)}
        onKeyDown={onTriggerKey}
        className={cn(
          "appearance-none w-full rounded-md bg-bg-base border text-fg text-left truncate",
          "focus-ring focus-visible:border-brand-500/50",
          "disabled:cursor-not-allowed",
          invalid ? "border-status-critical/50" : "border-line-subtle",
          SIZE_CLS[size],
        )}
        {...(rest as React.ButtonHTMLAttributes<HTMLButtonElement>)}
      >
        <span className={cn("block truncate", !selected && "text-fg-muted")}>
          {selected ? selected.label : " "}
        </span>
      </button>
      <ChevronDown
        size={12}
        aria-hidden
        className={cn(
          "absolute top-1/2 -translate-y-1/2 text-fg-muted pointer-events-none transition-transform",
          open && "rotate-180",
          CHEVRON_OFFSET[size],
        )}
      />

      {open &&
        pos &&
        createPortal(
          <div
            ref={panelRef}
            role="listbox"
            id={listId}
            tabIndex={-1}
            style={{
              position: "fixed",
              left: pos.left,
              top: pos.drop === "down" ? pos.top : undefined,
              bottom: pos.drop === "up" ? window.innerHeight - pos.top : undefined,
              width: pos.width,
              maxHeight: pos.maxH,
            }}
            className={cn(
              "z-[60] overflow-auto rounded-lg border border-line-subtle bg-bg-raised shadow-xl py-1",
              "animate-in fade-in-0 zoom-in-95",
            )}
          >
            {flat.length === 0 && (
              <div className="px-2.5 py-2 text-[13px] text-fg-muted">Geen opties</div>
            )}
            {groups.map((g, gi) => (
              <div key={gi} role="group" aria-label={g.label ?? undefined}>
                {g.label && (
                  <div className="px-2.5 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-fg-dim">
                    {g.label}
                  </div>
                )}
                {g.items.map((it) => {
                  const idx = flat.indexOf(it);
                  const isSel = it.value === current;
                  const isActive = idx === activeIdx;
                  return (
                    <div
                      key={it.value + ":" + idx}
                      data-idx={idx}
                      role="option"
                      aria-selected={isSel}
                      aria-disabled={it.disabled || undefined}
                      onMouseEnter={() => !it.disabled && setActiveIdx(idx)}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        if (it.disabled) return;
                        commit(it.value);
                        setOpen(false);
                        triggerRef.current?.focus();
                      }}
                      className={cn(
                        "flex items-center gap-2 px-2.5 py-1.5 text-[13px] cursor-pointer select-none",
                        it.disabled
                          ? "text-fg-muted opacity-50 cursor-not-allowed"
                          : "text-fg-dim",
                        isActive && !it.disabled && "bg-bg-elev text-fg",
                      )}
                    >
                      <Check
                        size={13}
                        className={cn("shrink-0 text-brand-500", isSel ? "opacity-100" : "opacity-0")}
                        aria-hidden
                      />
                      <span className="truncate">{it.label}</span>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
});
